import { useState, useRef, useEffect, useMemo } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useGlobalSearch, GlobalSearchFilters, SearchSectionId, SEARCH_SECTIONS } from "@/hooks/useGlobalSearch";
import { GlobalSearchFilterPopover } from "./GlobalSearchFilterPopover";
import { GlobalSearchResults } from "./GlobalSearchResults";

interface GlobalSearchBarProps {
  onSectionChange: (section: string, params?: any) => void;
  onGlobalFilterChange?: (filterTerm: string) => void;
}

// Default enabled sections - all sections active by default
const DEFAULT_SECTIONS: SearchSectionId[] = Object.keys(SEARCH_SECTIONS) as SearchSectionId[];

export const GlobalSearchBar = ({
  onSectionChange,
  onGlobalFilterChange,
}: GlobalSearchBarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filters, setFilters] = useState<GlobalSearchFilters>({
    enabledSections: new Set(DEFAULT_SECTIONS),
    kundentyp: 'all',
    leadheldenLead: 'all',
    exactMatch: false,
    fuzzyMatch: false,
  });
  
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // Use the new global search hook
  const { results, groupedResults, isLoading, totalCount } = useGlobalSearch(inputValue, {
    filters,
    limit: 10,
  });

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Global keyboard shortcut: Alt+G or Cmd+K to focus search
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isOurInput = target === inputRef.current;
      const isOtherInput = (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) && !isOurInput;
      
      if (isOtherInput) return;

      // Alt+G or Cmd+K: Open global search
      if ((e.altKey && e.key.toLowerCase() === 'g') || (e.metaKey && e.key.toLowerCase() === 'k')) {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const handleClear = () => {
    setInputValue("");
    inputRef.current?.focus();
  };

  const handleResultClick = (section: string, params?: any) => {
    // Include search term so target section can pre-fill local search
    // Use params.searchTerm if provided (e.g., document title), otherwise fall back to inputValue
    const enhancedParams = {
      ...params,
      searchTerm: params?.searchTerm || inputValue
    };
    onSectionChange(section, enhancedParams);
    setIsOpen(false);
    setInputValue("");
  };

  const handleClose = () => {
    setIsOpen(false);
    setInputValue("");
  };

  const placeholder = isMobile 
    ? "CRM durchsuchen..." 
    : "CRM durchsuchen... (Alt+G)";

  return (
    <div className="relative flex items-center gap-1 flex-1 min-w-0 max-w-[calc(100%-100px)] sm:max-w-none">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverAnchor asChild>
          <div className="relative w-full sm:w-[200px] md:w-[300px] lg:w-[400px] xl:w-[500px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={inputRef}
              placeholder={placeholder}
              value={inputValue}
              onClick={handleInputClick}
              onChange={(e) => {
                setInputValue(e.target.value);
                if (!isOpen) setIsOpen(true);
              }}
              className={cn("pl-10 w-full", inputValue && "pr-8")}
            />
            {inputValue && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </PopoverAnchor>
        
        <PopoverContent 
          className="w-[calc(100vw-2rem)] sm:w-[600px] p-0" 
          align="start"
          side="bottom"
          sideOffset={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          style={{ zIndex: 50 }}
        >
          <GlobalSearchResults
            results={results}
            groupedResults={groupedResults}
            isLoading={isLoading}
            searchTerm={inputValue}
            selectedIndex={selectedIndex}
            onSelectedIndexChange={setSelectedIndex}
            onResultClick={handleResultClick}
            onClose={handleClose}
          />
        </PopoverContent>
      </Popover>

      {/* Filter Button */}
      <GlobalSearchFilterPopover
        filters={filters}
        onFiltersChange={setFilters}
      />
    </div>
  );
};
